import React, { useState, useEffect } from "react";
import { Link, useHistory } from "react-router-dom";
import axios from "axios";
import swal from "sweetalert";
import { imageurl } from "../../../App";
import moment from "moment";
import { RiskBadge, needsCall } from "./OrderRisk";
import {
  FaSearch,
  FaFilter,
  FaFileDownload,
  FaPrint,
  FaTruck,
  FaCheckCircle,
  FaTimesCircle,
  FaClock,
  FaEye,
  FaUser,
  FaTag,
  FaEllipsisV
} from "react-icons/fa";

function orderPageNumbers(current, total) {
  if (total <= 7) {
    return Array.from({ length: total }, (_, index) => index + 1);
  }
  const nearby = [1, total, current - 1, current, current + 1].filter(
    (number) => number >= 1 && number <= total
  );
  const unique = [...new Set(nearby)].sort((a, b) => a - b);
  const pages = [];
  unique.forEach((number, index) => {
    if (index > 0 && number - unique[index - 1] > 1) {
      pages.push("gap-" + unique[index - 1]);
    }
    pages.push(number);
  });
  return pages;
}

export default function OrderView() {
  const history = useHistory();
  const [orderList, setOrderList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("all");
  const [searchQuery, setSearchQuery] = useState("");
  const [paymentFilter, setPaymentFilter] = useState("all");
  const [selectedOrders, setSelectedOrders] = useState([]);
  const [actionLoading, setActionLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);

  const fetchOrders = () => {
    setLoading(true);
    axios
      .get("/api/view-orders")
      .then((res) => {
        if (res.data.status === 200) {
          setOrderList(res.data.orders || []);
        }
        setLoading(false);
      })
      .catch(() => {
        setLoading(false);
      });
  };

  useEffect(() => {
    document.title = "Orders & Shipments Management — Admin";
    fetchOrders();
  }, []);

  const handleStatusUpdate = (orderId, newStatus) => {
    const targetOrder = orderList.find((o) => o.id === orderId);
    if (!targetOrder) return;

    setActionLoading(true);
    axios
      .post("/api/order-status-update", {
        orderStatus: newStatus,
        orderId: orderId,
        userId: targetOrder.user_id,
      })
      .then((res) => {
        setActionLoading(false);
        if (res.data.status === 200) {
          swal("Updated", res.data.message || "Order updated successfully", "success", {
            buttons: false,
            timer: 1200,
          });
          fetchOrders();
        } else {
          swal("Notice", res.data.message || "Could not update status", "info");
        }
      })
      .catch(() => {
        setActionLoading(false);
        swal("Error", "Failed to update order status", "error");
      });
  };

  // Filtered Orders Logic
  const filteredOrders = orderList.filter((ord) => {
    const q = searchQuery.toLowerCase().trim();
    const customerName = `${ord.firstname || ""} ${ord.lastname || ""}`.toLowerCase();
    const email = (ord.email || "").toLowerCase();
    const phone = (ord.phone || "").toLowerCase();
    const tracking = (ord.tracking_no || "").toLowerCase();
    const orderIdStr = String(ord.id);

    const matchesSearch =
      q === "" ||
      orderIdStr.includes(q) ||
      tracking.includes(q) ||
      customerName.includes(q) ||
      email.includes(q) ||
      phone.includes(q);

    if (!matchesSearch) return false;

    // Payment Filter
    if (paymentFilter !== "all" && ord.payment_mode !== paymentFilter) {
      return false;
    }

    const hasDate = (val) => Boolean(val && typeof val === "string" && !val.startsWith("0000") && val !== "null");

    // Tabs Filter
    const isCancelled = hasDate(ord.order_cancel) || ord.status_label === "Cancelled" || ord.status == 6 || ord.order_status === "Cancelled";
    const isDelivered = hasDate(ord.order_complete) || ord.status_label === "Delivered" || ord.status == 4 || ord.order_status === "Delivered";
    const isShipped = hasDate(ord.order_dispatch) || ord.status_label === "Shipped" || ord.status == 3 || ord.order_status === "Shipped" || ord.fulfillment_status === "Fulfilled";
    const isRefunded = ord.order_refund != null || ord.status == 5 || ord.status_label === "Refunded" || ord.order_status === "Refunded";
    const isUnfulfilled = !isShipped && !isDelivered && !isCancelled && !isRefunded;

    if (activeTab === "unfulfilled") return isUnfulfilled;
    if (activeTab === "callFirst") return isUnfulfilled && needsCall(ord.risk);
    if (activeTab === "shipped") return isShipped && !isDelivered && !isCancelled && !isRefunded;
    if (activeTab === "delivered") return isDelivered;
    if (activeTab === "cancelled") return isCancelled;
    if (activeTab === "refunded") return isRefunded;
    if (activeTab === "unpaid") return ord.payment_mode === "COD" && !isDelivered && !isCancelled && !isRefunded;

    return true;
  });

  // Tab counters
  const totalCount = orderList.length;
  const unfulfilledCount = orderList.filter((o) => !o.order_dispatch && !o.order_complete && !o.order_cancel && !o.order_refund && o.status != 5 && o.status != 6 && o.order_status !== "Refunded" && o.order_status !== "Cancelled").length;
  const callFirstCount = orderList.filter((o) => !o.order_dispatch && !o.order_complete && !o.order_cancel && !o.order_refund && o.status != 5 && o.status != 6 && o.order_status !== "Refunded" && o.order_status !== "Cancelled" && needsCall(o.risk)).length;
  const shippedCount = orderList.filter((o) => o.order_dispatch && !o.order_complete && !o.order_cancel && !o.order_refund && o.status != 5).length;
  const deliveredCount = orderList.filter((o) => o.order_complete || o.status == 4 || o.order_status === "Delivered").length;
  const cancelledCount = orderList.filter((o) => o.order_cancel || o.status == 6 || o.order_status === "Cancelled").length;
  const refundedCount = orderList.filter((o) => o.order_refund || o.status == 5 || o.order_status === "Refunded").length;

  const pageCount = Math.max(1, Math.ceil(filteredOrders.length / pageSize));
  const currentPage = Math.min(page, pageCount);
  const pageStart = (currentPage - 1) * pageSize;
  const visibleOrders = filteredOrders.slice(pageStart, pageStart + pageSize);
  const pageNumbers = orderPageNumbers(currentPage, pageCount);
  const pageAllSelected =
    visibleOrders.length > 0 && visibleOrders.every((order) => selectedOrders.includes(order.id));

  useEffect(() => {
    setPage(1);
  }, [activeTab, searchQuery, paymentFilter, pageSize]);

  const handleSelectAll = (e) => {
    const pageIds = visibleOrders.map((order) => order.id);
    if (e.target.checked) {
      setSelectedOrders([...new Set([...selectedOrders, ...pageIds])]);
    } else {
      setSelectedOrders(selectedOrders.filter((id) => !pageIds.includes(id)));
    }
  };

  const handleSelectOne = (id) => {
    if (selectedOrders.includes(id)) {
      setSelectedOrders(selectedOrders.filter((oid) => oid !== id));
    } else {
      setSelectedOrders([...selectedOrders, id]);
    }
  };

  const exportSelectedCSV = () => {
    const listToExport = selectedOrders.length > 0
      ? orderList.filter((o) => selectedOrders.includes(o.id))
      : filteredOrders;

    if (listToExport.length === 0) return;

    const headers = ["Order ID", "Tracking No", "Customer", "Email", "Phone", "City", "Payment Mode", "Total Amount (PKR)", "Status", "Created At"];
    const rows = listToExport.map((o) => [
      o.id,
      o.tracking_no,
      `"${o.firstname} ${o.lastname}"`,
      o.email,
      o.phone,
      o.city,
      o.payment_mode,
      o.total_amount,
      o.status_label || "Placed",
      `"${new Date(o.created_at).toISOString()}"`,
    ]);

    const csvContent = "data:text/csv;charset=utf-8," + [headers.join(","), ...rows.map((e) => e.join(","))].join("\n");
    const encodedUri = encodeURI(csvContent);
    const link = document.createElement("a");
    link.setAttribute("href", encodedUri);
    link.setAttribute("download", `orders_export_${new Date().toISOString().slice(0, 10)}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <div>
      {/* Page Header */}
      <div className="d-flex align-items-center justify-content-between flex-wrap gap-3 mb-4">
        <div>
          <h4 className="fw-bold mb-1" style={{ color: "var(--admin-text-main)" }}>
            Orders & Fulfillment Control
          </h4>
          <p className="text-muted mb-0" style={{ fontSize: "13.5px" }}>
            Monitor guest and registered customer orders, fulfill shipments, and handle returns
          </p>
        </div>

        <div className="d-flex align-items-center gap-2">
          <button
            type="button"
            onClick={exportSelectedCSV}
            className="btn btn-sm btn-light border rounded-pill px-3 py-2 fw-semibold d-flex align-items-center gap-2"
          >
            <FaFileDownload size={12} />
            <span>Export Orders ({selectedOrders.length ? selectedOrders.length : "All"})</span>
          </button>
        </div>
      </div>

      {/* Filter Tabs Bar (Shopify Style) */}
      <div className="admin-card mb-4 p-0">
        <div className="d-flex align-items-center gap-2 px-3 pt-3 border-bottom overflow-auto flex-nowrap" style={{ fontSize: "13.5px" }}>
          <button
            type="button"
            className={`btn btn-link text-decoration-none px-3 py-2 fw-bold border-bottom ${
              activeTab === "all" ? "text-primary border-primary border-3" : "text-muted border-transparent"
            }`}
            onClick={() => setActiveTab("all")}
          >
            All Orders <span className="badge bg-light text-dark ms-1">{totalCount}</span>
          </button>
          <button
            type="button"
            className={`btn btn-link text-decoration-none px-3 py-2 fw-bold border-bottom ${
              activeTab === "unfulfilled" ? "text-primary border-primary border-3" : "text-muted border-transparent"
            }`}
            onClick={() => setActiveTab("unfulfilled")}
          >
            Unfulfilled <span className="badge bg-warning-subtle text-warning ms-1">{unfulfilledCount}</span>
          </button>
          <button
            type="button"
            className={`btn btn-link text-decoration-none px-3 py-2 fw-bold border-bottom ${
              activeTab === "callFirst" ? "text-primary border-primary border-3" : "text-muted border-transparent"
            }`}
            onClick={() => setActiveTab("callFirst")}
            title="Not shipped yet, with warning signs. Call these customers before shipping."
          >
            Call first <span className={`badge ms-1 ${callFirstCount ? "bg-danger text-white" : "bg-light text-dark"}`}>{callFirstCount}</span>
          </button>
          <button
            type="button"
            className={`btn btn-link text-decoration-none px-3 py-2 fw-bold border-bottom ${
              activeTab === "shipped" ? "text-primary border-primary border-3" : "text-muted border-transparent"
            }`}
            onClick={() => setActiveTab("shipped")}
          >
            Shipped <span className="badge bg-info-subtle text-info ms-1">{shippedCount}</span>
          </button>
          <button
            type="button"
            className={`btn btn-link text-decoration-none px-3 py-2 fw-bold border-bottom ${
              activeTab === "delivered" ? "text-primary border-primary border-3" : "text-muted border-transparent"
            }`}
            onClick={() => setActiveTab("delivered")}
          >
            Delivered <span className="badge bg-success-subtle text-success ms-1">{deliveredCount}</span>
          </button>
          <button
            type="button"
            className={`btn btn-link text-decoration-none px-3 py-2 fw-bold border-bottom ${
              activeTab === "cancelled" ? "text-primary border-primary border-3" : "text-muted border-transparent"
            }`}
            onClick={() => setActiveTab("cancelled")}
          >
            Cancelled <span className="badge bg-danger-subtle text-danger ms-1">{cancelledCount}</span>
          </button>
          <button
            type="button"
            className={`btn btn-link text-decoration-none px-3 py-2 fw-bold border-bottom ${
              activeTab === "refunded" ? "text-primary border-primary border-3" : "text-muted border-transparent"
            }`}
            onClick={() => setActiveTab("refunded")}
          >
            Refunded <span className="badge bg-light text-dark ms-1">{refundedCount}</span>
          </button>
        </div>

        {/* Search and Filters Strip */}
        <div className="p-3 d-flex align-items-center justify-content-between flex-wrap gap-3 bg-light">
          <div className="d-flex align-items-center gap-2 flex-grow-1" style={{ maxWidth: "450px" }}>
            <div className="position-relative w-100">
              <input
                type="text"
                className="form-control form-control-sm ps-4"
                placeholder="Search by Order #ID, Tracking, Customer, Phone..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                style={{ borderRadius: "8px" }}
              />
              <span className="position-absolute start-0 top-50 translate-middle-y ms-2 text-muted" style={{ fontSize: "11px" }}>
                <FaSearch />
              </span>
            </div>
          </div>

          <div className="d-flex align-items-center gap-2">
            <select
              className="form-select form-select-sm"
              style={{ width: "160px", borderRadius: "8px" }}
              value={paymentFilter}
              onChange={(e) => setPaymentFilter(e.target.value)}
            >
              <option value="all">All Payments</option>
              <option value="COD">Cash on Delivery (COD)</option>
              <option value="BT">Bank transfer</option>
              <option value="JAZZCASH">JazzCash</option>
              <option value="EASYPAISA">Easypaisa</option>
            </select>
          </div>
        </div>

        {/* Orders Table */}
        <div className="table-responsive">
          {loading ? (
            <div className="p-5 text-center">
              <div className="spinner-border text-primary" role="status"></div>
              <p className="text-muted mt-2 fw-semibold">Loading orders...</p>
            </div>
          ) : filteredOrders.length === 0 ? (
            <div className="p-5 text-center">
              <FaTruck size={36} className="text-muted mb-2" />
              <h6 className="fw-bold">No Orders in this View</h6>
              <p className="text-muted mb-0" style={{ fontSize: "13px" }}>
                No orders match your filter criteria.
              </p>
            </div>
          ) : (
            <table className="admin-table">
              <thead>
                <tr>
                  <th style={{ width: "40px" }}>
                    <input
                      type="checkbox"
                      className="form-check-input"
                      checked={pageAllSelected}
                      onChange={handleSelectAll}
                    />
                  </th>
                  <th style={{ width: "90px" }}>Order</th>
                  <th>Date</th>
                  <th>Customer</th>
                  <th>City</th>
                  <th>Payment</th>
                  <th>Fulfillment</th>
                  <th className="text-center">Items</th>
                  <th className="text-end">Total Amount</th>
                  <th className="text-end" style={{ width: "180px" }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {visibleOrders.map((ord) => {
                  const hasDate = (val) => Boolean(val && typeof val === "string" && !val.startsWith("0000") && val !== "null");
                  const isDelivered = hasDate(ord.order_complete) || ord.status_label === "Delivered" || ord.status == 4 || ord.order_status === "Delivered";
                  const isShipped = hasDate(ord.order_dispatch) || ord.status_label === "Shipped" || ord.status == 3 || ord.order_status === "Shipped" || ord.fulfillment_status === "Fulfilled";
                  const isCancelled = hasDate(ord.order_cancel) || ord.status_label === "Cancelled" || ord.status == 6 || ord.order_status === "Cancelled";
                  const isRefunded = ord.order_refund != null || ord.status == 5 || ord.status_label === "Refunded" || ord.order_status === "Refunded";

                  return (
                    <tr key={ord.id} style={{ cursor: "pointer" }}>
                      <td onClick={(e) => e.stopPropagation()}>
                        <input
                          type="checkbox"
                          className="form-check-input"
                          checked={selectedOrders.includes(ord.id)}
                          onChange={() => handleSelectOne(ord.id)}
                        />
                      </td>
                      <td>
                        <Link
                          to={`/admin/orders/${ord.id}`}
                          className="fw-bold text-primary text-decoration-none d-block"
                        >
                          #{ord.id}
                        </Link>
                        <code className="text-muted" style={{ fontSize: "11px" }}>
                          {ord.tracking_no}
                        </code>
                      </td>
                      <td>
                        <small className="text-dark fw-semibold d-block">
                          {moment(ord.created_at).isValid() ? moment(ord.created_at).format("DD MMM, YYYY") : "—"}
                        </small>
                        <small className="text-muted">
                          {moment(ord.created_at).isValid() ? moment(ord.created_at).format("hh:mm A") : "—"}
                        </small>
                      </td>
                      <td>
                        <div className="d-flex flex-column" style={{ lineHeight: 1.2 }}>
                          <span className="fw-bold text-dark">
                            {ord.firstname} {ord.lastname}
                          </span>
                          <small className="text-muted">
                            {ord.user_id ? "Registered" : "Guest Shopper"}
                          </small>
                          <RiskBadge risk={ord.risk} />
                        </div>
                      </td>
                      <td>
                        <span className="text-muted" style={{ fontSize: "13px" }}>
                          {ord.city}
                        </span>
                      </td>
                      <td>
                        <span className="badge bg-light text-dark border">
                          {{ COD: "Cash on Delivery", BT: "Bank transfer", JAZZCASH: "JazzCash", EASYPAISA: "Easypaisa" }[ord.payment_mode] || ord.payment_mode}
                        </span>
                        {["JAZZCASH", "EASYPAISA"].includes(ord.payment_mode) && (
                          <span className={`badge d-block mt-1 ${ord.payment_status === "Paid" ? "bg-success" : "bg-warning text-dark"}`} style={{ width: "fit-content" }}>
                            {ord.payment_status === "Paid" ? "Paid online" : "Not paid yet"}
                          </span>
                        )}
                        {(ord.payment_mode === "BT" || ord.payment_mode === "Bank Transfer") && ord.payment_slip && (
                          <a
                            href={`${imageurl}/${String(ord.payment_slip).replace(/^\/+/, "")}`}
                            target="_blank"
                            rel="noreferrer"
                            onClick={(e) => e.stopPropagation()}
                            className="d-block mt-2"
                          >
                            <img
                              src={`${imageurl}/${String(ord.payment_slip).replace(/^\/+/, "")}`}
                              alt="Payment photo"
                              style={{ width: "72px", height: "72px", objectFit: "cover", borderRadius: "8px", border: "1px solid #e2e8f0", background: "#fff" }}
                            />
                          </a>
                        )}
                      </td>
                      <td>
                        {isCancelled ? (
                          <span className="admin-badge admin-badge-danger">Cancelled</span>
                        ) : isRefunded ? (
                          <span className="admin-badge admin-badge-danger">Refunded</span>
                        ) : isDelivered ? (
                          <span className="admin-badge admin-badge-success">Delivered</span>
                        ) : isShipped ? (
                          <span className="admin-badge admin-badge-info">Shipped</span>
                        ) : (
                          <span className="admin-badge admin-badge-warning">Unfulfilled</span>
                        )}
                      </td>
                      <td className="text-center">
                        <span className="badge bg-light text-dark border">
                          {ord.items_count || (ord.orderitems?.length || 1)}
                        </span>
                      </td>
                      <td className="text-end">
                        <span className="fw-bold text-dark" style={{ fontSize: "14px" }}>
                          Rs. {Number(ord.total_amount || 0).toLocaleString()}
                        </span>
                      </td>
                      <td className="text-end" onClick={(e) => e.stopPropagation()}>
                        <div className="d-inline-flex align-items-center gap-1">
                          <Link
                            to={`/admin/orders/${ord.id}`}
                            className="btn btn-sm btn-light border rounded-2"
                            title="Inspect Order Details"
                          >
                            <FaEye size={12} />
                          </Link>

                          {!isDelivered && !isCancelled && !isRefunded && (
                            <select
                              className="form-select form-select-sm"
                              style={{ width: "110px", fontSize: "11.5px" }}
                              defaultValue=""
                              onChange={(e) => {
                                if (e.target.value) {
                                  handleStatusUpdate(ord.id, e.target.value);
                                }
                              }}
                            >
                              <option value="" disabled>Actions...</option>
                              {!isShipped && <option value="OD">Fulfill / Ship</option>}
                              <option value="OA">Delivered</option>
                              <option value="OC">Cancel</option>
                            </select>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>

        {!loading && filteredOrders.length > 0 && (
          <div className="d-flex align-items-center justify-content-between flex-wrap gap-3 px-3 py-3 border-top">
            <div className="d-flex align-items-center gap-2 text-muted" style={{ fontSize: "13px" }}>
              <span>
                Showing {pageStart + 1}–{Math.min(pageStart + visibleOrders.length, filteredOrders.length)} of {filteredOrders.length}
              </span>
              <select
                className="form-select form-select-sm"
                style={{ width: "88px", borderRadius: "8px" }}
                value={pageSize}
                onChange={(e) => setPageSize(Number(e.target.value))}
                aria-label="Orders per page"
              >
                <option value={10}>10</option>
                <option value={20}>20</option>
                <option value={50}>50</option>
              </select>
            </div>
            {pageCount > 1 && (
              <nav aria-label="Orders pages">
                <ul className="pagination pagination-sm mb-0">
                  <li className={`page-item${currentPage === 1 ? " disabled" : ""}`}>
                    <button
                      type="button"
                      className="page-link"
                      disabled={currentPage === 1}
                      onClick={() => setPage(currentPage - 1)}
                    >
                      Previous
                    </button>
                  </li>
                  {pageNumbers.map((item) =>
                    typeof item === "string" ? (
                      <li key={item} className="page-item disabled">
                        <span className="page-link">…</span>
                      </li>
                    ) : (
                      <li key={item} className={`page-item${item === currentPage ? " active" : ""}`}>
                        <button type="button" className="page-link" onClick={() => setPage(item)}>
                          {item}
                        </button>
                      </li>
                    )
                  )}
                  <li className={`page-item${currentPage === pageCount ? " disabled" : ""}`}>
                    <button
                      type="button"
                      className="page-link"
                      disabled={currentPage === pageCount}
                      onClick={() => setPage(currentPage + 1)}
                    >
                      Next
                    </button>
                  </li>
                </ul>
              </nav>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
